import { useState, useEffect, useCallback } from "react";

type Theme = "light" | "dark";

const STORAGE_KEY = "smileygift-theme";
const TRANSITION_MS = 500;

export const useTheme = () => {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === "undefined") return "light";
    const stored = localStorage.getItem(STORAGE_KEY) as Theme | null;
    if (stored === "light" || stored === "dark") return stored;
    return "light";
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    // Create a full-screen overlay for smooth fade transition
    const overlay = document.createElement("div");
    Object.assign(overlay.style, {
      position: "fixed",
      inset: "0",
      zIndex: "99999",
      background: theme === "dark" ? "white" : "#0d1520",
      opacity: "0",
      pointerEvents: "none",
      transition: `opacity ${TRANSITION_MS / 2}ms ease-in-out`,
    });
    document.body.appendChild(overlay);

    // Fade in overlay
    requestAnimationFrame(() => {
      overlay.style.opacity = "0.6";
    });

    // At peak, switch theme
    setTimeout(() => {
      setThemeState((prev) => (prev === "dark" ? "light" : "dark"));

      // Fade out overlay
      requestAnimationFrame(() => {
        overlay.style.opacity = "0";
      });

      // Remove overlay after fade out
      setTimeout(() => {
        overlay.remove();
      }, TRANSITION_MS / 2);
    }, TRANSITION_MS / 2);
  }, [theme]);

  return { theme, toggleTheme, isDark: theme === "dark" };
};
